import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Text,
  useColorModeValue,
  Badge,
  VStack,
  HStack,
  Wrap,
  WrapItem,
  Checkbox,
  SimpleGrid,
  Input,
  Select,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { CloseIcon, AddIcon } from "@chakra-ui/icons";

const MONTHS = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' }
];

function MultiMonthSelector({ 
  selectedMonths = [], 
  onChange, 
  defaultYear = null,
  disabled = false,
  allowQuickSelect = true 
}) {
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  const [currentYear, setCurrentYear] = useState(
    defaultYear || new Date().getFullYear().toString()
  );

  // Función para obtener el valor completo YYYY-MM
  const getFullMonthValue = (monthValue) => `${currentYear}-${monthValue}`;

  // Verificar si un mes específico está seleccionado
  const isMonthSelected = (monthValue) => {
    const fullValue = getFullMonthValue(monthValue);
    return selectedMonths.includes(fullValue);
  };

  // Manejar selección/deselección de mes individual
  const handleMonthToggle = (monthValue) => {
    if (disabled) return;
    
    const fullValue = getFullMonthValue(monthValue);
    const newSelectedMonths = isMonthSelected(monthValue)
      ? selectedMonths.filter(month => month !== fullValue)
      : [...selectedMonths, fullValue].sort();
    
    onChange(newSelectedMonths);
  };

  // Seleccionar todos los meses del año actual
  const selectAllMonths = () => {
    if (disabled) return;
    
    const allMonthsThisYear = MONTHS.map(month => getFullMonthValue(month.value));
    const otherYearMonths = selectedMonths.filter(month => !month.startsWith(currentYear));
    onChange([...otherYearMonths, ...allMonthsThisYear].sort());
  };

  // Deseleccionar todos los meses del año actual
  const deselectAllMonths = () => {
    if (disabled) return;
    
    const otherYearMonths = selectedMonths.filter(month => !month.startsWith(currentYear));
    onChange(otherYearMonths);
  };

  // Verificar si todos los meses del año actual están seleccionados
  const areAllMonthsSelected = () => {
    return MONTHS.every(month => isMonthSelected(month.value));
  };

  // Cambiar año y actualizar selecciones
  const handleYearChange = (newYear) => {
    setCurrentYear(newYear);
  };

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel fontWeight="semibold" color={textColor}>
          Selección de meses
        </FormLabel>

        {/* Selector de año */}
        <HStack spacing={3} mb={4}>
          <Text fontSize="sm" color={textColor} minW="80px">
            Año:
          </Text>
          <Select
            value={currentYear}
            onChange={(e) => handleYearChange(e.target.value)}
            size="sm"
            width="120px"
            disabled={disabled}
          >
            {[2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map(year => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </Select>
          
          {allowQuickSelect && (
            <HStack spacing={2}>
              <Tooltip label="Seleccionar todos los meses del año">
                <IconButton
                  size="sm"
                  icon={<AddIcon />}
                  colorScheme="green"
                  variant="outline"
                  onClick={selectAllMonths}
                  disabled={disabled || areAllMonthsSelected()}
                  aria-label="Seleccionar todos los meses"
                />
              </Tooltip>
              
              <Tooltip label="Deseleccionar todos los meses del año">
                <IconButton
                  size="sm"
                  icon={<CloseIcon />}
                  colorScheme="red"
                  variant="outline"
                  onClick={deselectAllMonths}
                  disabled={disabled || selectedMonths.filter(m => m.startsWith(currentYear)).length === 0}
                  aria-label="Deseleccionar todos los meses"
                />
              </Tooltip>
            </HStack>
          )}
        </HStack>

        {/* Grid de meses */}
        <SimpleGrid columns={[2, 3, 4]} spacing={3} mb={4}>
          {MONTHS.map((month) => {
            const isSelected = isMonthSelected(month.value);
            const fullValue = getFullMonthValue(month.value);
            
            return (
              <Checkbox
                key={month.value}
                isChecked={isSelected}
                onChange={() => handleMonthToggle(month.value)}
                colorScheme="teal"
                size="lg"
                disabled={disabled}
                _hover={{
                  bg: !disabled ? bgColor : undefined
                }}
                p={2}
                borderRadius="md"
                border="1px"
                borderColor={isSelected ? "teal.200" : borderColor}
                bg={isSelected ? "teal.50" : "transparent"}
              >
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" fontWeight="medium">
                    {month.label}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {fullValue}
                  </Text>
                </VStack>
              </Checkbox>
            );
          })}
        </SimpleGrid>
      </FormControl>

      {/* Meses seleccionados - Vista resumida */}
      {selectedMonths.length > 0 && (
        <Box 
          p={3} 
          bg={bgColor} 
          borderRadius="md" 
          border="1px" 
          borderColor={borderColor}
        >
          <Text fontSize="sm" fontWeight="semibold" mb={2} color={textColor}>
            Meses seleccionados ({selectedMonths.length}):
          </Text>
          <Wrap spacing={2}>
            {selectedMonths.map((monthYear) => {
              const [year, month] = monthYear.split('-');
              const monthData = MONTHS.find(m => m.value === month);
              
              return (
                <WrapItem key={monthYear}>
                  <Badge 
                    colorScheme="teal" 
                    variant="solid" 
                    display="flex" 
                    alignItems="center"
                    gap={1}
                    px={2}
                    py={1}
                  >
                    <Text fontSize="xs">
                      {monthData?.label} {year}
                    </Text>
                    {!disabled && (
                      <IconButton
                        size="xs"
                        icon={<CloseIcon />}
                        variant="ghost"
                        colorScheme="white"
                        minW="auto"
                        h="auto"
                        p={0}
                        onClick={() => {
                          onChange(selectedMonths.filter(m => m !== monthYear));
                        }}
                        aria-label={`Remover ${monthData?.label} ${year}`}
                      />
                    )}
                  </Badge>
                </WrapItem>
              );
            })}
          </Wrap>
        </Box>
      )}

      {/* Información adicional */}
      {selectedMonths.length === 0 && (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
          Selecciona al menos un mes para generar los cobros
        </Text>
      )}
    </VStack>
  );
}

export default MultiMonthSelector;